import { useEffect, useMemo } from 'react';
import { analysisApi } from '../service';
import { useAppDispatch } from '../../useAppDispatch';
import { useSelector } from 'react-redux';
import { SavedAnalysisOverview } from '../types';
import { getOverviewApiEndDate, getOverviewApiStartDate } from '../utils';
import moment from 'moment';

/**
 * Filters the data that is not null from the endpoints and creates an object with the url as key and overview data as value
 */
function createUrlsRecord(data: (SavedAnalysisOverview[] | undefined)[], urls: string[], startDate: moment.Moment, endDate: moment.Moment) {
    return data.reduce((record, overviews, index) => {
        if (overviews && overviews.length > 0) {
            return {
                ...record,
                [urls[index]]: overviews.filter((overview) => moment.utc(overview.created_at).isBetween(startDate, endDate, 'day', '[]')),
            };
        } else {
            return record;
        }
    }, {} as Record<string, SavedAnalysisOverview[]>);
}

type Params = {
    urls: string[];
    startDate: moment.Moment;
    endDate: moment.Moment;
    pollingInterval?: number;
};

type MultipleOverviewsReturn = {
    data: Record<string, SavedAnalysisOverview[]>;
    isLoading: boolean;
};

/**
 * Simulate a useQuery hook that will fetch overview data using the fetchOverview endpoint for multiple urls.
 * This is beneficial because we can re-use cached data for any of the urls provided
 * @param urls
 * @param startDate
 * @param endDate
 * @returns
 */
export default function useFetchMultipleFilteredOverviews({ urls, startDate, endDate, pollingInterval }: Params): MultipleOverviewsReturn {
    const dispatch = useAppDispatch();

    // Create memoized selectors for each cache entry corresponding to an url
    const selectOverview = useMemo(
        () =>
            urls.map((url) =>
                analysisApi.endpoints.fetchOverviews.select({
                    url,
                    startDate: getOverviewApiStartDate(startDate),
                    endDate: getOverviewApiEndDate(endDate),
                })
            ),
        [startDate, endDate, urls]
    );

    // Select the caches with the memoized selectors
    const overviewQueryStates = useSelector((state) => urls.map((_, index) => selectOverview[index](state as any)));

    // If any of them is loading then the hook is loading
    const isLoadingMultipleUrlsOverviews = useMemo(() => overviewQueryStates.some((query) => query.isLoading), [overviewQueryStates]);

    // Group the data property from the queries into a key:value object, where key is the url and value is the overview list and filter by date
    const groupedUrlOverviews = useMemo(
        () =>
            createUrlsRecord(
                overviewQueryStates.map((query) => query.data),
                urls,
                startDate,
                endDate
            ),
        [overviewQueryStates, urls, startDate, endDate]
    );

    // Fetch data and cache it in order to be read by the selector. If there was already data this does not cause a refetch
    useEffect(() => {
        const subscriptions = urls.map((url) => {
            return dispatch(
                analysisApi.endpoints.fetchOverviews.initiate(
                    { url, startDate: getOverviewApiStartDate(startDate), endDate: getOverviewApiEndDate(endDate) },
                    { subscriptionOptions: { pollingInterval } }
                )
            );
        });

        return () => {
            subscriptions.forEach((subscription) => subscription.unsubscribe());
        };
    }, [dispatch, endDate, startDate, urls, pollingInterval]);

    return {
        data: groupedUrlOverviews,
        isLoading: isLoadingMultipleUrlsOverviews,
    };
}
